// Immediate header positioning to prevent CLS - runs before DOMContentLoaded
(function() {
  'use strict';
  function initHeaderPosition() {
    const sectionHeader = document.querySelector('.section-header');
    const announcementBar = document.querySelector('.announcement-bar-section');
    const headerWrapper = document.querySelector('.header-wrapper');
    
    if (!sectionHeader) {
      if (document.readyState === 'loading') {
        requestAnimationFrame(initHeaderPosition);
      }
      return;
    }
    
    // Check if transparent header is enabled
    const transparentHeaderEnabled = headerWrapper && (
      headerWrapper.getAttribute('data-transparent-header') === 'true' ||
      headerWrapper.classList.contains('transparent-header-enabled')
    );
    
    // Only apply absolute positioning when transparent header is enabled
    if (transparentHeaderEnabled) {
      sectionHeader.classList.add('transparent-header-overlay');
      
      // Reset header position on page load - always calculate from top, not scroll position
      // This prevents header from being stuck at wrong position when page reloads while scrolled
      if (announcementBar && announcementBar.offsetHeight) {
        const announcementBarHeight = announcementBar.offsetHeight;
        document.documentElement.style.setProperty('--announcement-bar-height', announcementBarHeight + 'px');
        // Only set inline style if not sticky (sticky uses CSS variable)
        if (!sectionHeader.classList.contains('shopify-section-header-sticky')) {
          sectionHeader.style.top = announcementBarHeight + 'px';
        }
      } else {
        document.documentElement.style.setProperty('--announcement-bar-height', '0');
        if (!sectionHeader.classList.contains('shopify-section-header-sticky')) {
          sectionHeader.style.top = '0';
        }
      }
    } else {
      // Transparent header disabled - remove overlay positioning
      sectionHeader.classList.remove('transparent-header-overlay');
      sectionHeader.style.top = '';
      sectionHeader.style.position = '';
    }
    
    if (sectionHeader.offsetHeight) {
      document.documentElement.style.setProperty('--header-height', sectionHeader.offsetHeight + 'px');
    }
  }
  
  // Run immediately if DOM is ready, otherwise wait
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderPosition);
  } else {
    initHeaderPosition();
  }
  
  // Also try immediately in case elements exist
  requestAnimationFrame(initHeaderPosition);
  
  // Reset header position on page load/refresh to prevent stuck position
  window.addEventListener('load', function() {
    requestAnimationFrame(initHeaderPosition);
  });
})();

const BOOST_SEARCH_CLOSE_DELAY_MS = 250;
let boostSearchCloseTimeoutId = null;

const BOOST_SEARCH_UI_SELECTOR =
  '[class*="boost-sd__instant-search"], [class*="boost-sd__search-result"], [class*="boost-sd__search-suggestion"], .boost-sd__search-widget-init-wrapper, [id^="boost-sd__search-widget"]';

function simulateEscapeKey(target) {
  if (!target) return;
  const init = { key: 'Escape', code: 'Escape', keyCode: 27, which: 27, bubbles: true, cancelable: true };
  target.dispatchEvent(new KeyboardEvent('keydown', init));
  target.dispatchEvent(new KeyboardEvent('keyup', init));
}

function getDesktopSearchToggle(searchBar, toggleButton) {
  return (
    toggleButton ||
    searchBar?.closest('.desktop-search-wrapper, .compact-search-wrapper')?.querySelector('[data-search-toggle]') ||
    searchBar?.closest('.header__icons.desktop')?.querySelector('[data-search-toggle]')
  );
}

function getHeaderSearchInputs(searchBar) {
  const inputs = [];
  const addInput = (el) => {
    if (el?.tagName === 'INPUT' && !inputs.includes(el)) inputs.push(el);
  };

  if (searchBar) {
    searchBar.querySelectorAll('input[type="search"], input[name="q"], .boost-sd__search-widget-init-input, [id^="boost-sd__search-widget-init-input"]').forEach(addInput);
  }

  document.querySelectorAll('#boost-sd__search-widget-init-input-0, input.boost-sd__search-widget-init-input').forEach(addInput);
  return inputs;
}

function dispatchEscapeForBoostSearch(searchBar) {
  const targets = new Set([document, document.body, searchBar].filter(Boolean));
  getHeaderSearchInputs(searchBar).forEach((input) => targets.add(input));
  if (document.activeElement) targets.add(document.activeElement);
  document.querySelectorAll(BOOST_SEARCH_UI_SELECTOR).forEach((el) => targets.add(el));
  targets.forEach(simulateEscapeKey);
}

function isDesktopHeaderSearchOpen(searchBar) {
  return Boolean(searchBar?.classList.contains('search-active'));
}

function openDesktopHeaderSearch(searchBar, toggleButton) {
  if (!searchBar) return;

  if (boostSearchCloseTimeoutId) {
    clearTimeout(boostSearchCloseTimeoutId);
    boostSearchCloseTimeoutId = null;
  }

  const toggle = getDesktopSearchToggle(searchBar, toggleButton);
  searchBar.classList.add('search-active');
  if (toggle) toggle.setAttribute('aria-expanded', 'true');

  requestAnimationFrame(() => {
    const input = getHeaderSearchInputs(searchBar)[0] || searchBar.querySelector('input');
    input?.focus();
  });
}

function closeDesktopHeaderSearch(searchBar, toggleButton, options = {}) {
  if (!searchBar) return;

  const { simulateEscape = false, focusToggle = false } = options;
  const toggle = getDesktopSearchToggle(searchBar, toggleButton);

  getHeaderSearchInputs(searchBar).forEach((input) => {
    if (document.activeElement === input) input.blur();
  });

  const predictiveSearch = searchBar.querySelector('predictive-search');
  if (predictiveSearch && typeof predictiveSearch.close === 'function') {
    predictiveSearch.close(true);
  }

  searchBar.classList.remove('search-active');
  if (toggle) toggle.setAttribute('aria-expanded', 'false');

  if (simulateEscape) {
    if (boostSearchCloseTimeoutId) clearTimeout(boostSearchCloseTimeoutId);
    boostSearchCloseTimeoutId = setTimeout(() => {
      boostSearchCloseTimeoutId = null;
      dispatchEscapeForBoostSearch(searchBar);
    }, BOOST_SEARCH_CLOSE_DELAY_MS);
  }

  if (focusToggle) toggle?.focus();
}

function getOpenDesktopSearchBars(root = document) {
  return new Set(root.querySelectorAll('.header__icons.desktop .header-search.search-active'));
}

function isBoostSearchUiElement(target) {
  return Boolean(target?.closest(BOOST_SEARCH_UI_SELECTOR));
}

(function initDesktopHeaderSearchToggle() {
  if (window.__desktopHeaderSearchToggleInit) return;
  window.__desktopHeaderSearchToggleInit = true;

  document.addEventListener(
    'click',
    function (event) {
      const toggleButton = event.target.closest('.header__icons.desktop [data-search-toggle]');
      if (!toggleButton) return;

      const searchBar = toggleButton.closest('.header__icons.desktop')?.querySelector('.header-search');
      if (!searchBar) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (isDesktopHeaderSearchOpen(searchBar)) {
        closeDesktopHeaderSearch(searchBar, toggleButton, { simulateEscape: true, focusToggle: true });
        return;
      }

      getOpenDesktopSearchBars().forEach((bar) => {
        if (bar !== searchBar) closeDesktopHeaderSearch(bar, null, { simulateEscape: true });
      });
      openDesktopHeaderSearch(searchBar, toggleButton);
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      const barsToClose = getOpenDesktopSearchBars();
      if (!barsToClose.size) return;

      const toggleButtons = document.querySelectorAll('.header__icons.desktop [data-search-toggle]');
      const isInsideSearch = Array.from(barsToClose).some((bar) => bar.contains(event.target));
      const isInsideToggle = Array.from(toggleButtons).some((btn) => btn.contains(event.target));

      if (!isInsideSearch && !isInsideToggle && !isBoostSearchUiElement(event.target)) {
        barsToClose.forEach((bar) => closeDesktopHeaderSearch(bar, null, { simulateEscape: true }));
      }
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    const barsToClose = getOpenDesktopSearchBars();
    if (!barsToClose.size) return;

    const focusedInSearch = document.activeElement?.closest?.('.header-search');
    barsToClose.forEach((bar) => closeDesktopHeaderSearch(bar));
    if (focusedInSearch) {
      focusedInSearch.closest('.header__icons.desktop')?.querySelector('[data-search-toggle]')?.focus();
    }
  });
})();

class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.scrollThreshold = 40;
    this.scrollRemoveThreshold = 30;
    this.resizeTimeout = null;
    this.scrollTimeout = null;
    this.scrolledStateTimeout = null;
    this.isPositionInitialized = false;
    this.lastScrollTop = 0;
    this.scrollThresholdPx = 0;
    this.expandedHeaderHeight = 0;
    this.lastStickyTop = null;
    this.scrolledStateDebounceCompactMs = 0;
    this.scrolledStateDebounceFullMs = 50;
    this.scrolledStateInitialized = false;
    this.slideAnimationMs = 300;
    this.slideOutTimeout = null;
    this.compactBufferPx = 300;
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerWrapper = this.querySelector('.header-wrapper') || document.querySelector('.header-wrapper');
    this.headerElement = this.querySelector('.header');
    this.compactHeader = this.querySelector('.compact-header');
    this.bottomMenu = this.querySelector('.bottom-desktop-menu') || document.querySelector('.bottom-desktop-menu');
    this.announcementBar = document.querySelector('.announcement-bar-section');

    this.stickyType = this.getAttribute('data-sticky-type');
    this.headerIsAlwaysSticky = this.stickyType === 'always' ||
                                 this.stickyType === 'reduce-logo-size';
    this.enableScrollHideShow = this.stickyType === 'scroll-hide-show';
    this.transparentHeaderEnabled = this.getAttribute('data-transparent-header') === 'true' ||
                                    this.headerWrapper?.classList.contains('transparent-header-enabled');
    this.isHomePage = this.getAttribute('data-home-page') === 'true';
    this.headerBounds = {};
    this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.desktopMQ = window.matchMedia('(min-width: 993px)');
    this.isDesktop = this.desktopMQ.matches;
    this.desktopMQHandler = (e) => {
      this.isDesktop = e.matches;
      this.reinitializeForBreakpoint();
    };
    this.desktopMQ.addEventListener('change', this.desktopMQHandler);

    this.initHeaderPosition();
    this.setHeaderHeight();
    window.matchMedia('(max-width: 992px)').addEventListener('change', () => {
      this.setHeaderHeight();
      this.initHeaderPosition();
    });

    if (this.headerIsAlwaysSticky) {
      this.initStickyMode();
    } else if (this.enableScrollHideShow) {
      this.header.classList.add('shopify-section-header-sticky');
      requestAnimationFrame(() => {
        this.updateStickyPosition();
      });
    }

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => this.preventReveal = true;
    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    window.addEventListener('resize', this.handleResize.bind(this), { passive: true });
    this.createObserver();

    requestAnimationFrame(() => {
      this.handleTransparentScroll();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (scrollTop <= 10) {
        this.header.classList.remove('shopify-section-header-hidden', 'scrolled-past-header');
        this.reset();
      } else {
        this.currentScrollTop = scrollTop;
      }

      if (this.header && this.header.classList.contains('shopify-section-header-sticky')) {
        this.updateStickyPosition();
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
    window.removeEventListener('resize', this.handleResize);
    if (this.desktopMQ && this.desktopMQHandler) {
      this.desktopMQ.removeEventListener('change', this.desktopMQHandler);
    }
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    if (this.scrollTimeout) cancelAnimationFrame(this.scrollTimeout);
    if (this.scrolledStateTimeout) clearTimeout(this.scrolledStateTimeout);
    if (this.slideOutTimeout) clearTimeout(this.slideOutTimeout);
  }

  initStickyMode() {
    if (!this.header) return;
    if (this.isDesktop) {
      this.header.classList.remove('shopify-section-header-sticky');
      this.header.classList.add('section-header--scroll-up-mode');
    } else {
      this.header.classList.remove(
        'section-header--scroll-up-mode', 'section-header--compact-sticky', 'section-header--compact-visible'
      );
      if (this.transparentHeaderEnabled && this.isHomePage) {
        this.header.classList.remove('shopify-section-header-sticky');
      } else {
        this.header.classList.add('shopify-section-header-sticky');
        requestAnimationFrame(() => this.updateStickyPosition());
      }
    }
  }

  reinitializeForBreakpoint() {
    if (!this.header || !this.headerIsAlwaysSticky) return;
    this.scrolledStateInitialized = false;
    this.headerWrapper?.classList.remove('scrolled');
    this.bottomMenu?.classList.remove('scrolled');
    if (this.compactHeader) {
      this.compactHeader.classList.remove('compact-header--hide-immediate');
      this.compactHeader.setAttribute('aria-hidden', 'false');
      if (this.headerElement) this.headerElement.removeAttribute('aria-hidden');
    }
    this.header.classList.remove(
      'section-header--scroll-up-mode', 'section-header--compact-sticky', 'section-header--compact-visible',
      'shopify-section-header-sticky'
    );
    this.header.style.minHeight = '';
    if (this.slideOutTimeout) {
      clearTimeout(this.slideOutTimeout);
      this.slideOutTimeout = null;
    }
    if (this.headerSpacer) {
      this.headerSpacer.style.height = '0';
    }
    this.initStickyMode();
    this.initHeaderPosition();
    this.setHeaderHeight();
    requestAnimationFrame(() => this.handleTransparentScroll());
  }

  initHeaderPosition() {
    if (!this.header) return;

    requestAnimationFrame(() => {
      if (this.isDesktop && this.header.classList.contains('section-header--compact-sticky')) {
        return;
      }

      if (!this.transparentHeaderEnabled) {
        this.header.classList.remove('transparent-header-overlay');
        this.header.style.top = '';
        this.header.style.position = '';
        this.setHeaderHeight();
        this.isPositionInitialized = true;
        return;
      }

      this.header.classList.add('transparent-header-overlay');

      if (this.announcementBar && this.announcementBar.offsetHeight) {
        const announcementBarHeight = this.announcementBar.offsetHeight;
        document.documentElement.style.setProperty('--announcement-bar-height', `${announcementBarHeight}px`);
        if (!this.header.classList.contains('shopify-section-header-sticky') &&
            !this.header.classList.contains('section-header--compact-sticky')) {
          this.header.style.top = `${announcementBarHeight}px`;
        }
      } else {
        document.documentElement.style.setProperty('--announcement-bar-height', '0');
        if (!this.header.classList.contains('shopify-section-header-sticky') &&
            !this.header.classList.contains('section-header--compact-sticky')) {
          this.header.style.top = '0';
        }
      }

      this.setHeaderHeight();
      this.isPositionInitialized = true;
    });
  }

  setHeaderHeight() {
    if (this.header) {
      const headerHeight = this.header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }
    const isScrolled = this.headerWrapper?.classList.contains('scrolled');
    const isScrolledWithCompact = isScrolled && this.compactHeader;

    // Main menu: sticky-header-height
    document.documentElement.style.setProperty('--sticky-header-height', `${this.offsetHeight}px`);

    // Compact menu: separate variable – distance from details top to summary bottom (mega menu sits right below menu item, no gap)
    if (this.compactHeader) {
      
      let compactTop = this.compactHeader.offsetHeight;
      // if (isScrolledWithCompact) {
      //   const details = this.compactHeader.querySelector('details.mega-menu');
      //   const summary = details?.querySelector('summary');
      //   if (details && summary) {
      //     const detailsRect = details.getBoundingClientRect();
      //     const summaryRect = summary.getBoundingClientRect();
      //     compactTop = Math.ceil(summaryRect.bottom - detailsRect.top);
      //   }
      // }
      document.documentElement.style.setProperty('--compact-sticky-header-height', `${compactTop}px`);

      // Shop megamenu (compact + image bg): top = compact header bottom minus Shop details top (full-bleed panel; see tmf-custom.css)
      if (
        isScrolledWithCompact &&
        this.compactHeader.classList.contains('compact-header--image-bg')
      ) {
        const shopDetails = this.compactHeader.querySelector('#Details-HeaderMenu-compact-shop');
        if (shopDetails) {
          const chRect = this.compactHeader.getBoundingClientRect();
          const dRect = shopDetails.getBoundingClientRect();
          const shopTop = Math.round(chRect.bottom - dRect.top);
          document.documentElement.style.setProperty('--compact-megamenu-top-shop', `${shopTop}px`);
        } else {
          document.documentElement.style.removeProperty('--compact-megamenu-top-shop');
        }
      } else {
        document.documentElement.style.removeProperty('--compact-megamenu-top-shop');
      }
    } else {
      document.documentElement.style.removeProperty('--compact-megamenu-top-shop');
      document.documentElement.style.removeProperty('--compact-sticky-header-height');
    }
    if (this.headerWrapper && !this.headerWrapper.classList.contains('scrolled') && this.offsetHeight > 0) {
      this.expandedHeaderHeight = this.offsetHeight;
    }
    if (getOpenShopMegaMenuDetails()) {
      updateOpenMegaMenuAvailableHeight();
    }
  }

  applyScrolledState(shouldBeScrolled, immediate = false) {
    if (!this.headerWrapper) {
      this.headerWrapper = this.querySelector('.header-wrapper') || document.querySelector('.header-wrapper');
    }
    if (!this.bottomMenu) {
      this.bottomMenu = this.querySelector('.bottom-desktop-menu') || document.querySelector('.bottom-desktop-menu');
    }
    if (this.headerWrapper) {
      if (shouldBeScrolled) {
        this.applyScrolledStateCompact(immediate);
      } else {
        this.applyScrolledStateFull();
      }
    }
  }

  applyScrolledStateCompact(immediate = false) {
    if (this.expandedHeaderHeight <= 0) this.expandedHeaderHeight = this.offsetHeight;
    if (!this.header) return;

    if (this.isDesktop && this.headerIsAlwaysSticky) {
      if (this.slideOutTimeout) {
        clearTimeout(this.slideOutTimeout);
        this.slideOutTimeout = null;
      }

      if (!this.compactHeader) {
        if (!this.headerSpacer) {
          this.headerSpacer = document.createElement('div');
          this.headerSpacer.className = 'header-spacer';
          this.headerSpacer.setAttribute('aria-hidden', 'true');
          this.header.insertAdjacentElement('afterend', this.headerSpacer);
        }
        this.headerSpacer.style.height = this.expandedHeaderHeight + 'px';
      }

      this.headerWrapper.classList.add('scrolled');

      if (this.compactHeader) {
        this.compactHeader.setAttribute('aria-hidden', 'false');
        if (this.headerElement) this.headerElement.setAttribute('aria-hidden', 'true');
      } else {
        if (this.bottomMenu) this.bottomMenu.classList.add('scrolled');
      }

      if (immediate) {
        this.header.classList.add('section-header--compact-sticky', 'section-header--compact-visible');
      } else {
        this.header.classList.add('section-header--compact-sticky');
        void this.header.offsetHeight;
        setTimeout(() => {
          this.header.classList.add('section-header--compact-visible');
        }, 50);
      }

      requestAnimationFrame(() => this.setHeaderHeight());
    } else {
      this.headerWrapper.classList.add('scrolled');
      if (this.bottomMenu) this.bottomMenu.classList.add('scrolled');
      if (this.transparentHeaderEnabled && this.isHomePage) {
        this.header.classList.add('shopify-section-header-sticky');
        requestAnimationFrame(() => this.updateStickyPosition());
      }
      requestAnimationFrame(() => this.setHeaderHeight());
    }
  }

  applyScrolledStateFull() {
    if (!this.header) return;

    if (this.isDesktop && this.headerIsAlwaysSticky) {
      if (this.slideOutTimeout) {
        clearTimeout(this.slideOutTimeout);
        this.slideOutTimeout = null;
      }

      const doFullReset = () => {
        this.header.classList.remove('section-header--compact-sticky');
        this.headerWrapper.classList.remove('scrolled');
        if (this.compactHeader) {
          this.compactHeader.classList.remove('compact-header--hide-immediate');
          this.compactHeader.setAttribute('aria-hidden', 'false');
          if (this.headerElement) this.headerElement.removeAttribute('aria-hidden');
        } else {
          if (this.bottomMenu) this.bottomMenu.classList.remove('scrolled');
          if (this.headerSpacer) this.headerSpacer.style.height = '0';
        }
        this.header.style.minHeight = '';
        this.initHeaderPosition();
        requestAnimationFrame(() => this.setHeaderHeight());
      };

      if (this.compactHeader) {
        this.compactHeader.classList.add('compact-header--hide-immediate');
      }
      doFullReset();
    } else {
      this.headerWrapper.classList.remove('scrolled');
      if (this.compactHeader) {
        this.compactHeader.setAttribute('aria-hidden', 'false');
        if (this.headerElement) this.headerElement.removeAttribute('aria-hidden');
      } else if (this.bottomMenu) {
        this.bottomMenu.classList.remove('scrolled');
      }
      if (this.transparentHeaderEnabled && this.isHomePage) {
        this.header.classList.remove('shopify-section-header-sticky');
      }
      if (this.header) this.header.style.minHeight = '';
      requestAnimationFrame(() => this.setHeaderHeight());
    }
  }

  getCompactThreshold() {
    if (this.isDesktop && this.headerIsAlwaysSticky && this.expandedHeaderHeight > 0) {
      return this.expandedHeaderHeight + this.compactBufferPx;
    }
    if (this.headerIsAlwaysSticky && this.expandedHeaderHeight > 0) {
      return this.expandedHeaderHeight;
    }
    return this.scrollThreshold;
  }

  getFullThreshold() {
    if (this.headerIsAlwaysSticky) {
      const compact = this.getCompactThreshold();
      return Math.max(0, compact - 50);
    }
    return this.scrollRemoveThreshold;
  }

  handleTransparentScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const shouldApplyScrolled = this.transparentHeaderEnabled || this.headerIsAlwaysSticky;
    if (!shouldApplyScrolled) return;

    if (!this.headerWrapper) {
      this.headerWrapper = this.querySelector('.header-wrapper') || document.querySelector('.header-wrapper');
    }
    const isCurrentlyScrolled = this.headerWrapper && this.headerWrapper.classList.contains('scrolled');

    if (!this.scrolledStateInitialized) {
      this.scrolledStateInitialized = true;
      const compactThreshold = this.getCompactThreshold();
      this.applyScrolledState(scrollTop > compactThreshold, true);
      return;
    }

    if (this.scrolledStateTimeout) {
      clearTimeout(this.scrolledStateTimeout);
      this.scrolledStateTimeout = null;
    }

    const compactThreshold = this.getCompactThreshold();
    const fullThreshold = this.getFullThreshold();
    const wantScrolled = scrollTop > compactThreshold;
    const wantFull = scrollTop <= fullThreshold;

    if (wantScrolled && !isCurrentlyScrolled) {
      this.scrolledStateTimeout = setTimeout(() => {
        this.scrolledStateTimeout = null;
        const top = window.pageYOffset || document.documentElement.scrollTop;
        if (top > compactThreshold) this.applyScrolledState(true);
      }, this.scrolledStateDebounceCompactMs);
    } else if (wantFull && isCurrentlyScrolled) {
      this.applyScrolledState(false);
    }
  }

  updateStickyPosition() {
    if (this.isDesktop && this.header && this.header.classList.contains('section-header--compact-sticky')) {
      return;
    }

    const isSticky = this.header && (
      this.header.classList.contains('shopify-section-header-sticky') ||
      this.header.classList.contains('section-header--compact-sticky')
    );
    if (!this.header || !isSticky) return;

    if (!this.isDesktop) {
      this.header.style.removeProperty('top');
      document.documentElement.style.setProperty('--announcement-bar-height', this.announcementBar?.offsetHeight ? `${this.announcementBar.offsetHeight}px` : '0');
      return;
    }

    if (!this.transparentHeaderEnabled) {
      if (this.lastStickyTop !== 0) {
        this.header.style.top = '0';
        this.lastStickyTop = 0;
      }
      return;
    }

    if (this.announcementBar) {
      const announcementBarBottom = this.announcementBar.getBoundingClientRect().bottom;
      const announcementBarHeight = this.announcementBar.offsetHeight;
      const newTop = announcementBarBottom > 0 ? Math.round(announcementBarBottom) : 0;
      if (this.lastStickyTop !== newTop) {
        this.header.style.top = newTop > 0 ? `${newTop}px` : '0';
        this.lastStickyTop = newTop;
      }
      document.documentElement.style.setProperty('--announcement-bar-height', announcementBarBottom > 0 ? `${announcementBarHeight}px` : '0');
    } else {
      if (this.lastStickyTop !== 0) {
        this.header.style.top = '0';
        this.lastStickyTop = 0;
      }
      document.documentElement.style.setProperty('--announcement-bar-height', '0');
    }
  }

  handleResize() {
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.initHeaderPosition();
      this.updateStickyPosition();
    }, 150);
  }

  createObserver() {
    if (!this.header) return;
    
    const headerRect = this.header.getBoundingClientRect();
    this.headerBounds = {
      top: headerRect.top + window.pageYOffset,
      bottom: headerRect.bottom + window.pageYOffset,
      height: headerRect.height
    };
    
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].intersectionRect) {
        this.headerBounds = entries[0].intersectionRect;
      }
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    if (this.scrollTimeout) return;
    this.scrollTimeout = requestAnimationFrame(() => {
      this.scrollTimeout = null;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      this.handleTransparentScroll();
      this.updateStickyPosition();

      if (this.predictiveSearch && this.predictiveSearch.isOpen) {
        this.currentScrollTop = scrollTop;
        return;
      }

      const hideActivationThreshold = 160;

      if (scrollTop <= 10) {
        this.header.classList.remove('scrolled-past-header');
        if (this.enableScrollHideShow && this.header.classList.contains('shopify-section-header-hidden')) {
          requestAnimationFrame(this.reveal.bind(this));
        } else if (!this.enableScrollHideShow) {
          requestAnimationFrame(this.reset.bind(this));
        }
        this.currentScrollTop = scrollTop;
        return;
      }

      if (this.enableScrollHideShow) {
        this.header.classList.add('shopify-section-header-sticky', 'scrolled-past-header');

        const scrollDiff = scrollTop - this.currentScrollTop;

        if (scrollDiff >= this.scrollThresholdPx && scrollTop > hideActivationThreshold) {
          if (!this.header.classList.contains('shopify-section-header-hidden')) {
            requestAnimationFrame(this.hide.bind(this));
          }
        } else if (scrollDiff <= -this.scrollThresholdPx) {
          if (this.header.classList.contains('shopify-section-header-hidden')) {
            requestAnimationFrame(this.reveal.bind(this));
          }
        }
      }

      this.currentScrollTop = scrollTop;
    });
  }

  hide() {
    if (!this.enableScrollHideShow) return;
    
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    if (!this.header.classList.contains('shopify-section-header-sticky')) {
      this.header.classList.add('shopify-section-header-sticky');
    }
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    if (!this.enableScrollHideShow) return;
    
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
    requestAnimationFrame(() => {
      this.updateStickyPosition();
    });
  }

  reset() {
    this.header.classList.remove('shopify-section-header-hidden', 'animate');
    
    if (this.enableScrollHideShow) {
      this.header.classList.remove('shopify-section-header-sticky');
    }
    this.initHeaderPosition();
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach(disclosure => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    if (this.searchModal) this.searchModal.close(false);
  }
}

const SHOP_MEGA_MENU_DETAILS_IDS = [
  'Details-HeaderMenu-shop',
  'Details-HeaderMenu-compact-shop',
  'Details-HeaderMenu-commercial-shop',
];

function getOpenShopMegaMenuDetails() {
  return SHOP_MEGA_MENU_DETAILS_IDS
    .map((id) => document.getElementById(id))
    .find((details) => details?.hasAttribute('open'));
}

function updateMegaMenuAvailableHeight(detailsEl) {
  const content = detailsEl?.querySelector('.mega-menu__content');
  if (!content) return;
  const top = content.getBoundingClientRect().top;
  const available = Math.max(0, window.innerHeight - top);
  document.documentElement.style.setProperty('--mega-menu-available-height', `${available}px`);
}

function clearMegaMenuAvailableHeight() {
  document.documentElement.style.removeProperty('--mega-menu-available-height');
}

function updateOpenMegaMenuAvailableHeight() {
  const openDetails = getOpenShopMegaMenuDetails();
  if (openDetails) {
    requestAnimationFrame(() => updateMegaMenuAvailableHeight(openDetails));
  } else {
    clearMegaMenuAvailableHeight();
  }
}

function initShopMegaMenuScrollHeight() {
  if (window.__shopMegaMenuScrollHeightInit) return;
  window.__shopMegaMenuScrollHeightInit = true;

  SHOP_MEGA_MENU_DETAILS_IDS.forEach((id) => {
    const details = document.getElementById(id);
    if (!details) return;
    details.addEventListener('toggle', () => {
      if (details.hasAttribute('open')) {
        requestAnimationFrame(() => updateMegaMenuAvailableHeight(details));
      } else if (!getOpenShopMegaMenuDetails()) {
        clearMegaMenuAvailableHeight();
      }
    });
  });

  window.addEventListener('resize', () => {
    updateOpenMegaMenuAvailableHeight();
  }, { passive: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShopMegaMenuScrollHeight);
} else {
  initShopMegaMenuScrollHeight();
}

// Handle transparent header for regular div
(function() {
  'use strict';

  function initTransparentHeaderForDiv() {
    const headerWrapper = document.querySelector('.header-wrapper');
    const stickyHeaderElement = document.querySelector('sticky-header');

    if (!headerWrapper || stickyHeaderElement || window.__transparentHeaderDivInit) {
      return;
    }
    window.__transparentHeaderDivInit = true;

    // Check if transparent header is enabled
    const transparentHeaderEnabled = headerWrapper.getAttribute('data-transparent-header') === 'true' ||
                                     headerWrapper.classList.contains('transparent-header-enabled');

    if (!transparentHeaderEnabled) {
      return;
    }

    const sectionHeader = document.querySelector('.section-header');
    const bottomMenu = document.querySelector('.bottom-desktop-menu');
    const scrollThreshold = 40;

    function handleTransparentScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const shouldBeScrolled = scrollTop > scrollThreshold;

      if (headerWrapper) {
        if (shouldBeScrolled) {
          headerWrapper.classList.add('scrolled');
        } else {
          headerWrapper.classList.remove('scrolled');
        }
      }

      if (bottomMenu) {
        if (shouldBeScrolled) {
          bottomMenu.classList.add('scrolled');
        } else {
          bottomMenu.classList.remove('scrolled');
        }
      }
    }

    // Set initial scroll state
    handleTransparentScroll();

    // Listen for scroll events
    let scrollTimeout;
    window.addEventListener('scroll', function() {
      if (scrollTimeout) {
        cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = requestAnimationFrame(handleTransparentScroll);
    }, { passive: true });

    // Handle resize events
    let resizeTimeout;
    window.addEventListener('resize', function() {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(function() {
        handleTransparentScroll();
      }, 250);
    });

  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTransparentHeaderForDiv);
  } else {
    initTransparentHeaderForDiv();
  }
})();

customElements.define('sticky-header', StickyHeader);